import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import MaintenancePage from './pages/auth/MaintenancePage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import PendingVerificationPage from './pages/auth/PendingVerificationPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForceChangePasswordPage from './pages/auth/ForceChangePasswordPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import InstructorDashboard from './pages/instructor/InstructorDashboard';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentGuide from './pages/help/ParentGuide';
import InstructorGuide from './pages/help/InstructorGuide';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import './index.css';
import './theme.css';

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return {
    week: Math.ceil((((d - yearStart) / 86400000) + 1) / 7),
    year: d.getUTCFullYear(),
  };
}

const BANNER_H = 44;

const PROMO_CLOSED_KEY = 'promo_banner_closed';

function PromoBanner() {
  const { user, loading } = useAuth();
  const { week } = getISOWeek(new Date());
  const isActiveWeek = week % 2 === 0; // show on even ISO weeks

  const [visible, setVisible] = useState(() => {
    if (!isActiveWeek) return false;
    if (getImpersonating()) return true; // always show during impersonation
    return sessionStorage.getItem(PROMO_CLOSED_KEY) !== '1';
  });

  const isAdmin = user?.role === 'admin';

  // Sync visible state with sessionStorage on every auth state change:
  // - logged out → clear the key so the next login starts fresh
  // - logged in  → if key is gone (fresh login), show the banner again
  useEffect(() => {
    if (loading) return;
    if (!user) {
      sessionStorage.removeItem(PROMO_CLOSED_KEY);
      return;
    }
    if (!isAdmin && isActiveWeek && sessionStorage.getItem(PROMO_CLOSED_KEY) !== '1') {
      setVisible(true);
    }
  }, [user, loading, isAdmin, isActiveWeek]);

  useEffect(() => {
    const val = visible && !isAdmin ? `${BANNER_H}px` : '0px';
    document.documentElement.style.setProperty('--banner-h', val);
    return () => document.documentElement.style.setProperty('--banner-h', '0px');
  }, [visible, isAdmin]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(PROMO_CLOSED_KEY, '1');
    setVisible(false);
  }, []);

  if (!visible || isAdmin) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10001,
      background: 'linear-gradient(90deg, #0d76ad, #20a0dc 34%, #d22490 68%, #9f246a)', color: '#ffffff',
      height: `${BANNER_H}px`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontFamily: 'Rajdhani, sans-serif',
      fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.09em', padding: '0 48px',
    }}>
      <a
        href="https://shopwowfactor.com/collections/georgia-fanwear-store"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#ffffff', textDecoration: 'none', textAlign: 'center',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        WOW FACTOR SHOP — New Items Available! SHOP NOW -&gt;
      </a>
      <button
        onClick={dismiss}
        aria-label="Close promotion banner"
        style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(0,0,0,0.14)', border: 'none', cursor: 'pointer',
          color: '#ffffff', width: 28, height: 28, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', fontWeight: 700, lineHeight: 1,
          transition: 'background 150ms ease',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.28)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.14)'}
      >
        &#10005;
      </button>
    </div>
  );
}

const getImpersonating = () => {
  const match = document.cookie.match(/(?:^|;\s*)wf_impersonating=([^;]+)/);
  if (!match) return null;
  try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

function ImpersonationBanner() {
  const [data, setData] = useState(null);
  useEffect(() => { setData(getImpersonating()); }, []);
  if (!data) return null;
  const end = async () => {
    await axios.post('/api/auth/impersonate/end');
    window.location.href = '/admin/users';
  };
  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9999, background:'#7c3aed', color:'#fff', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', fontFamily:'DM Sans, sans-serif', fontSize:'0.875rem', boxShadow:'0 -2px 12px rgba(124,58,237,0.5)' }}>
      <span>Viewing as <strong>{data.name}</strong> <span style={{ opacity:0.7 }}>({data.role})</span></span>
      <button onClick={end} style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.5)', color:'#fff', padding:'6px 16px', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'0.8rem' }}>
        Return to Admin
      </button>
    </div>
  );
}

const RoleRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'instructor') return <Navigate to="/instructor" replace />;
  return <Navigate to="/dashboard" replace />;
};

function LoadingScreen() {
  return (
    <div className="brand-loader">
      <div className="brand-loader-inner">
        <span className="brand-loader-mark"><img src="/wf-logo.png" alt="" /></span>
        <span>Preparing your training portal</span>
      </div>
    </div>
  );
}

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && !getImpersonating() && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  if (!user.email_verified && window.location.pathname !== '/pending-verification') {
    return <Navigate to="/pending-verification" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};


function AppContent() {
  const { maintenance, user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (maintenance && (!user || user.role !== 'admin')) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/help/parent" element={<ParentGuide />} />
        <Route path="/help/instructor" element={<InstructorGuide />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="*" element={<MaintenancePage />} />
      </Routes>
    );
  }

  return (
    <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/pending-verification" element={<ProtectedRoute><PendingVerificationPage /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ForceChangePasswordPage /></ProtectedRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/help/parent" element={<ParentGuide />} />
          <Route path="/help/instructor" element={<InstructorGuide />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/" element={<RoleRedirect />} />
          <Route path="/dashboard/*" element={<ProtectedRoute roles={['parent']}><ParentDashboard /></ProtectedRoute>} />
          <Route path="/instructor/*" element={<ProtectedRoute roles={['instructor']}><InstructorDashboard /></ProtectedRoute>} />
          <Route path="/admin/*" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PromoBanner />
        <AppContent />
        <ImpersonationBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}export default App;
